import { useState, useEffect, useRef } from 'react'
import { X, Save, Package, Search, ExternalLink } from '@/ui/icons'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Textarea } from '@/ui/components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/components'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Badge } from '@/ui/components'
import { CreateVehicleModificationDto, UpdateVehicleModificationDto, ModificationCategory, GearBrand, VehicleModification } from '@/shared/api'
import { VehicleModificationApi } from '@/shared/api'
import { useAuth } from "@/modules/auth"

interface AddModificationModalProps {
  isOpen: boolean
  onClose: () => void
  vehicleId: string
  onModificationAdded: (modification: any) => void
  editingModification?: VehicleModification | null
  initialCategory?: ModificationCategory
}

const MODIFICATION_CATEGORIES: { value: ModificationCategory; label: string }[] = [
  { value: 'armor', label: 'Armor' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'storage', label: 'Storage' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'tires_wheels', label: 'Tires & Wheels' },
  { value: 'roof_rack', label: 'Roof Rack' },
  { value: 'roof_top_tent', label: 'Roof Top Tent' },
  { value: 'truck_camper', label: 'Truck Camper' },
  { value: 'other', label: 'Other' }
]

export function AddModificationModal({ 
  isOpen, 
  onClose, 
  vehicleId, 
  onModificationAdded,
  editingModification,
  initialCategory
}: AddModificationModalProps) {
  const { getAccessToken } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [brands, setBrands] = useState<GearBrand[]>([])
  const [selectedCategory, setSelectedCategory] = useState<ModificationCategory>(initialCategory || 'recovery')
  const [brandSearchQuery, setBrandSearchQuery] = useState('')
  const [showBrandDropdown, setShowBrandDropdown] = useState(false)
  const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>(undefined)
  const brandDropdownRef = useRef<HTMLDivElement>(null)
  
  const [formData, setFormData] = useState<CreateVehicleModificationDto>({
    vehicle_id: vehicleId,
    category: initialCategory || 'recovery',
    product_name: '',
    product_model: '',
    description: '',
    purchase_price: undefined,
    notes: ''
  })

  const vehicleModificationApi = new VehicleModificationApi(getAccessToken)

  // Load editing data when modal opens or editingModification changes
  useEffect(() => {
    if (isOpen && editingModification) {
      setFormData({
        vehicle_id: vehicleId,
        category: editingModification.category,
        brand_id: editingModification.brand_id,
        custom_brand_name: editingModification.custom_brand_name,
        product_name: editingModification.product_name,
        product_model: editingModification.product_model || '',
        description: editingModification.description || '',
        purchase_price: editingModification.purchase_price,
        notes: editingModification.notes || ''
      })
      setSelectedCategory(editingModification.category)
      setSelectedBrandId(editingModification.brand_id)
      setBrandSearchQuery('')
    } else if (isOpen && !editingModification) {
      // Reset form when adding new
      const category = initialCategory || 'recovery'
      setFormData({
        vehicle_id: vehicleId,
        category: category,
        product_name: '',
        product_model: '',
        description: '',
        purchase_price: undefined,
        notes: ''
      })
      setSelectedCategory(category)
      setSelectedBrandId(undefined)
      setBrandSearchQuery('')
    }
  }, [isOpen, editingModification, vehicleId, initialCategory])

  // Load brands when category changes
  useEffect(() => {
    if (isOpen && selectedCategory) {
      loadBrands(selectedCategory)
    }
  }, [isOpen, selectedCategory])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target as Node)) {
        setShowBrandDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter brands based on search query
  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase())
  )

  // Sort brands: partners first, then alphabetically
  const sortedBrands = [...filteredBrands].sort((a, b) => {
    if (a.is_partner && !b.is_partner) return -1
    if (!a.is_partner && b.is_partner) return 1
    return a.name.localeCompare(b.name)
  })

  const loadBrands = async (category: ModificationCategory) => {
    try {
      const data = await vehicleModificationApi.getGearBrandsByCategory(category)
      setBrands(data)
    } catch (error) {
      setBrands([])
    }
  }

  const handleInputChange = (field: keyof CreateVehicleModificationDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Update category and reload brands if category changed
    if (field === 'category') {
      setSelectedCategory(value as ModificationCategory)
    }
  }

  const handleBrandSelect = (brandId: string | 'custom') => {
    if (brandId === 'custom') {
      setSelectedBrandId(undefined)
      setFormData(prev => ({ ...prev, brand_id: undefined, custom_brand_name: '' }))
      setBrandSearchQuery('')
    } else {
      const brand = brands.find(b => b.id === brandId)
      setSelectedBrandId(brandId)
      setFormData(prev => ({ ...prev, brand_id: brandId, custom_brand_name: undefined }))
      setBrandSearchQuery(brand?.name || '')
    }
    setShowBrandDropdown(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.product_name.trim()) {
      setError('Product name is required')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let modification: VehicleModification
      
      if (editingModification) {
        // Update existing modification
        const updateData: UpdateVehicleModificationDto = {
          category: formData.category,
          brand_id: formData.brand_id,
          custom_brand_name: formData.custom_brand_name,
          product_name: formData.product_name,
          product_model: formData.product_model,
          description: formData.description,
          purchase_price: formData.purchase_price,
          notes: formData.notes
        }
        modification = await vehicleModificationApi.updateModification(editingModification.id, updateData)
      } else {
        // Create new modification
        modification = await vehicleModificationApi.createModification(formData)
      }
      
      onModificationAdded(modification)
      onClose()
      
      // Reset form
      if (!editingModification) {
        setFormData({
          vehicle_id: vehicleId,
          category: 'recovery',
          product_name: '',
          product_model: '',
          description: '',
          purchase_price: undefined,
          notes: ''
        })
        setSelectedCategory('recovery')
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : editingModification ? 'Failed to update modification' : 'Failed to create modification')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center space-x-2">
            <Package className="w-5 h-5" />
            <span>{editingModification ? 'Edit Modification' : 'Add Vehicle Modification'}</span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={isLoading}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Category *</label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => handleInputChange('category', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {MODIFICATION_CATEGORIES.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Brand - Searchable */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Brand</label>
                <div className="relative" ref={brandDropdownRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={showBrandDropdown ? brandSearchQuery : (formData.brand_id ? brands.find(b => b.id === formData.brand_id)?.name || '' : formData.custom_brand_name || '')}
                      onChange={(e) => {
                        setBrandSearchQuery(e.target.value)
                        setShowBrandDropdown(true)
                        if (!e.target.value && formData.brand_id) {
                          setSelectedBrandId(undefined)
                          setFormData(prev => ({ ...prev, brand_id: undefined, custom_brand_name: undefined }))
                        }
                      }}
                      onFocus={() => {
                        setShowBrandDropdown(true)
                        if (formData.brand_id || formData.custom_brand_name) {
                          setBrandSearchQuery('')
                        }
                      }}
                      placeholder={formData.brand_id || formData.custom_brand_name ? "Click to change brand" : "Search brands or enter custom..."}
                      className="pl-9"
                    />
                  </div>
                  {showBrandDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      <div
                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b"
                        onClick={() => handleBrandSelect('custom')}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Custom Brand</span>
                          <span className="text-xs text-gray-500">Enter your own</span>
                        </div>
                      </div>
                      {sortedBrands.length > 0 ? (
                        sortedBrands.map((brand) => (
                          <div
                            key={brand.id}
                            className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                            onClick={() => handleBrandSelect(brand.id)}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {brand.logo_url && (
                                <img
                                  src={brand.logo_url}
                                  alt={brand.name}
                                  className="w-5 h-5 object-contain flex-shrink-0"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none'
                                  }}
                                />
                              )}
                              <span className="truncate">{brand.name}</span>
                              {brand.is_partner && (
                                <Badge variant="outline" className="text-xs ml-1 flex-shrink-0">
                                  Partner
                                </Badge>
                              )}
                            </div>
                            {brand.website && (
                              <a
                                href={brand.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        ))
                      ) : brandSearchQuery && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No brands found. Select "Custom Brand" to enter your own.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Custom Brand Name */}
            {!formData.brand_id && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Custom Brand Name</label>
                <Input
                  value={formData.custom_brand_name || ''}
                  onChange={(e) => handleInputChange('custom_brand_name', e.target.value)}
                  placeholder="Enter brand name"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Product Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Product Name *</label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => handleInputChange('product_name', e.target.value)}
                  placeholder="e.g., VR EVO 10-S Winch"
                  required
                />
              </div>

              {/* Product Model */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Product Model</label>
                <Input
                  value={formData.product_model || ''}
                  onChange={(e) => handleInputChange('product_model', e.target.value)}
                  placeholder="e.g., VR EVO 10-S"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe the modification..."
                rows={3}
              />
            </div>

            {/* Purchase Price */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Purchase Price ($)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.purchase_price || ''}
                onChange={(e) => handleInputChange('purchase_price', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="0.00"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingModification ? 'Saving...' : 'Adding...'}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingModification ? 'Save Changes' : 'Add Modification'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
